import { cloneDeep, get, isEmpty, set } from 'lodash'
import * as yup from 'yup'
import {
  CONDITIONS_INPUTS_LIST,
  GENERAL_INPUTS_LIST,
  GRANT_CONTROLS_INPUTS_LIST,
  INPUT_KEYS,
  INPUT_LIST,
  INPUT_POLICY_PATH,
} from '../policy_feature/inputs'
import {
  BASE_APPLICATIONS,
  BASE_INCLUDE_APPLICATIONS,
  BASE_INCLUDE_LOCATIONS,
  BASE_INCLUDE_USERS,
  BASE_LOCATIONS,
  BASE_USERS,
  DEFAULT_EMPTY_POLICY,
} from './constants'

const POLICY_UPDATE_STATUS = {
  PENDING: 'PENDING',
}

export const getPolicyStatusOptions = (intl) => [
  {
    label: intl.formatMessage({ id: 'GENERAL.ENABLED' }),
    value: 'enabled',
  },
  {
    label: intl.formatMessage({ id: 'GENERAL.DISABLED' }),
    value: 'disabled',
  },
  {
    label: intl.formatMessage({ id: 'POLICY_TEMPLATES.REPORT_ONLY' }),
    value: 'enabledForReportingButNotEnforced',
  },
]

export const getPolicyStatusMessage = ({ intl, value }) => {
  const options = getPolicyStatusOptions(intl)
  return options.find((option) => option.value === value)?.label
}

export const isPolicySyncing = (policy) => policy?.updateStatus === POLICY_UPDATE_STATUS.PENDING

export const isPolicyEmpty = (rawPolicy) => isEmpty(rawPolicy?.conditions)

/**
 * Checks if an auth strength id is global (non tenant specific)
 * @param {string} authStrengthId
 * @returns true if it's a non tenant specific auth strength, false otherwise
 */
export const isGlobalAuthStrength = (authStrengthId) => authStrengthId?.includes('00000000-0000-0000-0000')

/**
 * Transform values to an array so it can be looped the same way as others
 * Currently all input values are either an string or an array of strings
 */
export const toArray = (values) => {
  if (!values) {
    return []
  }
  if (values && !Array.isArray(values)) {
    return [values]
  }
  return values
}

/**
 * Verify if input values have tenant specific properties
 */
export const hasTenantSpecificValues = (input, value, globalFormOptionsMap) => {
  const arrayValues = toArray(value)

  if (!arrayValues?.length) {
    return false
  }

  // check if some input value is not in the global values map
  return arrayValues.some((val) => !globalFormOptionsMap[input][val])
}

/**
 * Removes tenant specific values from the policy object for the templates
 */
export const filterTenantProperties = (rawPolicy) => {
  const policy = cloneDeep(rawPolicy || {})

  const toMap = (array) => new Map(array.map((item) => [item.value, true]))

  const GLOBAL_INCLUDE_USERS = toMap(BASE_INCLUDE_USERS)

  const GLOBAL_EXCLUDE_USERS = toMap(BASE_USERS)

  const GLOBAL_INCLUDE_LOCATIONS = toMap(BASE_INCLUDE_LOCATIONS)

  const GLOBAL_EXCLUDE_LOCATIONS = toMap(BASE_LOCATIONS)

  const GLOBAL_INCLUDE_APPLICATIONS = toMap(BASE_INCLUDE_APPLICATIONS)

  const GLOBAL_EXCLUDE_APPLICATIONS = toMap(BASE_APPLICATIONS)

  // Users
  if (policy?.conditions?.users?.includeUsers?.length > 0) {
    policy.conditions.users.includeUsers = policy.conditions.users.includeUsers.filter((user) =>
      GLOBAL_INCLUDE_USERS.get(user),
    )
  }

  if (policy?.conditions?.users?.excludeUsers?.length > 0) {
    policy.conditions.users.excludeUsers = policy.conditions.users.excludeUsers.filter((user) =>
      GLOBAL_EXCLUDE_USERS.get(user),
    )
  }

  // Groups - no globals exist, remove all properties
  if (policy?.conditions?.users?.includeGroups?.length > 0) {
    policy.conditions.users.includeGroups = []
  }

  if (policy?.conditions?.users?.excludeGroups?.length > 0) {
    policy.conditions.users.excludeGroups = []
  }

  // guest or external users
  if (policy?.conditions?.users?.includeGuestsOrExternalUsers?.guestOrExternalUserTypes) {
    // we only support 'all' membership kind now
    policy.conditions.users.includeGuestsOrExternalUsers.externalTenants = { membershipKind: 'all' }
  }

  // guest or external users
  if (policy?.conditions?.users?.excludeGuestsOrExternalUsers?.guestOrExternalUserTypes) {
    // we only support 'all' membership kind now
    policy.conditions.users.excludeGuestsOrExternalUsers.externalTenants = { membershipKind: 'all' }
  }

  // Locations (include has global properties and exclude has no globals)
  if (policy?.conditions?.locations?.includeLocations?.length > 0) {
    policy.conditions.locations.includeLocations = policy.conditions.locations.includeLocations.filter((loc) =>
      GLOBAL_INCLUDE_LOCATIONS.get(loc),
    )
  }

  if (policy?.conditions?.locations?.excludeLocations?.length > 0) {
    policy.conditions.locations.excludeLocations = policy.conditions.locations.excludeLocations.filter((loc) =>
      GLOBAL_EXCLUDE_LOCATIONS.get(loc),
    )
  }

  // Applications
  if (policy?.conditions?.applications?.includeApplications?.length > 0) {
    policy.conditions.applications.includeApplications = policy.conditions.applications.includeApplications.filter(
      (app) => GLOBAL_INCLUDE_APPLICATIONS.get(app),
    )
  }

  if (policy?.conditions?.applications?.excludeApplications?.length > 0) {
    policy.conditions.applications.excludeApplications = policy.conditions.applications.excludeApplications.filter(
      (app) => GLOBAL_EXCLUDE_APPLICATIONS.get(app),
    )
  }

  // grant controls
  if (policy?.grantControls?.customAuthenticationFactors) {
    policy.grantControls.customAuthenticationFactors = []
  }

  const authStrengthId = policy?.grantControls?.authenticationStrength?.id
  if (authStrengthId) {
    if (isGlobalAuthStrength(authStrengthId)) {
      // clean rest of the fields, keep only the id
      policy.grantControls.authenticationStrength = { id: authStrengthId }
    } else {
      // tenant specific auth strength
      policy.grantControls.authenticationStrength = null
    }
  }

  return policy
}

// helper for the fillMinimumRequiredValues method
// sets the default value of a given path into the policy
const setPolicyDefault = (policy, path) => {
  set(policy, path, get(DEFAULT_EMPTY_POLICY, path))
}

/**
 * add defaults for minimum required fields that are empty
 * https://learn.microsoft.com/en-us/graph/api/conditionalaccessroot-post-policies?view=graph-rest-1.0&tabs=http#request-body
 * @param {object} rawPolicy
 */
export const fillMinimumRequiredValues = (rawPolicy) => {
  const policy = cloneDeep(rawPolicy || {})

  // for the users and applications it only matters for MS if we have one include rule in it
  // if policy has only excludes is still invalid for MS
  if (
    !policy?.conditions?.users?.includeUsers?.length &&
    !policy?.conditions?.users?.includeGroups?.length &&
    !policy?.conditions?.users?.includeRoles?.length &&
    !policy?.conditions?.users?.includeGuestsOrExternalUsers
  ) {
    setPolicyDefault(policy, 'conditions.users.includeUsers')
  }

  if (
    !policy?.conditions?.applications?.includeApplications?.length &&
    !policy?.conditions?.applications?.includeAuthenticationContextClassReferences?.length &&
    !policy?.conditions?.applications?.includeUserActions?.length
  ) {
    setPolicyDefault(policy, 'conditions.applications.includeApplications')
  }

  if (!policy?.conditions?.clientAppTypes?.length) {
    setPolicyDefault(policy, 'conditions.clientAppTypes')
  }

  if (!policy?.grantControls && !policy.sessionControls) {
    setPolicyDefault(policy, 'grantControls')
  }

  return policy
}

export const cleanPolicyObject = (policy, options = {}) => {
  const { removeEmptyValues = true } = options
  const result = cloneDeep(policy)

  // Housekeeping items
  // make sure to delete these properties even if they are null
  delete result.id
  delete result.createdDateTime
  delete result.modifiedDateTime
  delete result.displayName
  delete result.templateId
  delete result?.conditions?.users?.includeGuestsOrExternalUsers?.externalTenants?.['@odata.type']
  delete result?.conditions?.users?.excludeGuestsOrExternalUsers?.externalTenants?.['@odata.type']

  // Cleanup the top level of the policy
  Object.keys(result).forEach((body) => {
    if (removeEmptyValues && result[body] === null) {
      delete result[body]
    }
  })

  // Cleanup the conditions
  if (result.conditions) {
    Object.keys(result.conditions).forEach((condition) => {
      if (result.conditions[condition]) {
        Object.keys(result.conditions[condition]).forEach((key) => {
          // If any of a condition's children are empty then remove them
          if (
            removeEmptyValues &&
            Array.isArray(result.conditions[condition][key]) &&
            result.conditions[condition][key]?.length === 0
          ) {
            delete result.conditions[condition][key]

            // If any of a condition's children are null then remove them
          } else if (removeEmptyValues && result.conditions[condition][key] === null) {
            delete result.conditions[condition][key]
          }
        })

        // If the condition has no children then remove it
        if (removeEmptyValues && Object.keys(result.conditions[condition])?.length === 0) {
          delete result.conditions[condition]
        }

        // If the condition is null then remove it
      } else if (removeEmptyValues && result.conditions[condition] === null) {
        delete result.conditions[condition]
      }
    })
  }

  // Clean up grant controls
  if (result.grantControls) {
    if (result.grantControls['authenticationStrength@odata.context']) {
      // @TODO: Do we need to update the backend validation for this field instead of pruning it?
      delete result.grantControls['authenticationStrength@odata.context']
    }

    const authStrengthId = result?.grantControls?.authenticationStrength?.id
    if (authStrengthId) {
      // clean rest of the authenticationStrength fields, keep only the id
      result.grantControls.authenticationStrength = { id: authStrengthId }
    }

    Object.keys(result.grantControls).forEach((control) => {
      if (
        removeEmptyValues &&
        Array.isArray(result.grantControls[control]) &&
        result.grantControls[control]?.length === 0
      ) {
        delete result.grantControls[control]
      } else if (removeEmptyValues && result.grantControls[control] === null) {
        delete result.grantControls[control]
      }
    })
  }

  return result
}

/**
 * Transform a raw policy into our form object
 * This function is the inverse transformation of buildPolicyPayload
 * @param {*} rawPolicy
 */
export const toFormValues = (rawPolicy) => {
  const formValues = {}

  INPUT_LIST.forEach((input) => {
    const inputValue = get(rawPolicy, INPUT_POLICY_PATH[input])

    let parsedValue = inputValue
    // microsoft formats these inputs as strings of comma separated values, instead of being array for some reason
    if (
      [
        INPUT_KEYS.insiderRiskLevels,
        INPUT_KEYS.transferMethods,
        INPUT_KEYS.includeGuestsOrExternalUsers,
        INPUT_KEYS.excludeGuestsOrExternalUsers,
      ].includes(input)
    ) {
      parsedValue = inputValue?.split(',')
    } else if (input === INPUT_KEYS.includeUserActions) {
      // MS has includeUserActions as an array but only allows selecting one option
      // so in our app it's formatted as a single select input (string)
      parsedValue = inputValue?.[0]
    }

    formValues[input] = parsedValue
  })

  formValues.policyName = rawPolicy?.displayName
  formValues.policyStatus = rawPolicy?.state

  return formValues
}

/**
 * Apply the filled form values to the json policy object
 * This function is the inverse transformation of toFormValues
 * @param {*} rawPolicy
 * @param {*} formValues
 * @returns the updated policy
 */
export const buildPolicyPayload = (rawPolicy = {}, formValues, options = {}) => {
  const policy = cloneDeep(rawPolicy)

  // build policy skeleton (if empty)
  if (!policy.conditions) {
    policy.conditions = {}
  }

  if (!policy.conditions.users) {
    policy.conditions.users = {}
  }

  if (!policy.conditions.locations) {
    policy.conditions.locations = {}
  }

  if (!policy.conditions.applications) {
    policy.conditions.applications = {}
  }

  // replace the policy values with the form values
  INPUT_LIST.forEach((input) => {
    const value = formValues?.[input]

    // if value is undefined means input is not in the template so ignore
    // however if value is null or an empty array we should apply it
    if (value !== undefined) {
      let path = INPUT_POLICY_PATH[input]
      let formattedValue = value

      if (
        [
          INPUT_KEYS.insiderRiskLevels,
          INPUT_KEYS.transferMethods,
          INPUT_KEYS.includeGuestsOrExternalUsers,
          INPUT_KEYS.excludeGuestsOrExternalUsers,
        ].includes(input)
      ) {
        // microsoft formats these inputs as strings of comma separated values, instead of being array for some reason
        formattedValue = value.join(',')
      } else if (input === INPUT_KEYS.authenticationStrength && value === null) {
        // if authentication strength is unselected, object needs to be null
        path = 'grantControls.authenticationStrength'
      } else if (input === INPUT_KEYS.includeUserActions) {
        // MS has includeUserActions as an array but only allows selecting one option
        // so in our app it's formatted as a single select input (string)
        formattedValue = value ? [value] : []
      }

      set(policy, path, formattedValue)
    }
  })

  // locations object has to be null if no location is set
  const includeLocations = get(policy, INPUT_POLICY_PATH[INPUT_KEYS.includeLocations])
  const excludeLocations = get(policy, INPUT_POLICY_PATH[INPUT_KEYS.excludeLocations])
  if (isEmpty(includeLocations) && isEmpty(excludeLocations)) {
    policy.conditions.locations = null
  }

  // authenticationFlows object has to be null if no transferMethods is set
  const transferMethods = get(policy, INPUT_POLICY_PATH[INPUT_KEYS.transferMethods])
  if (!transferMethods) {
    policy.conditions.authenticationFlows = null
  }

  const includeGuestsOrExternalUsers = get(policy, INPUT_POLICY_PATH[INPUT_KEYS.includeGuestsOrExternalUsers])
  if (!includeGuestsOrExternalUsers) {
    // object has to be null if no guestOrExternalUserTypes set
    policy.conditions.users.includeGuestsOrExternalUsers = null
  } else if (!policy.conditions.users.includeGuestsOrExternalUsers.externalTenants) {
    // membership kind has to be set if guestOrExternalUserTypes exists
    policy.conditions.users.includeGuestsOrExternalUsers.externalTenants = { membershipKind: 'all' }
  }

  // apply same of the above for the exclude
  const excludeGuestsOrExternalUsers = get(policy, INPUT_POLICY_PATH[INPUT_KEYS.excludeGuestsOrExternalUsers])
  if (!excludeGuestsOrExternalUsers) {
    policy.conditions.users.excludeGuestsOrExternalUsers = null
  } else if (!policy.conditions.users.excludeGuestsOrExternalUsers.externalTenants) {
    policy.conditions.users.excludeGuestsOrExternalUsers.externalTenants = { membershipKind: 'all' }
  }

  // clean policy
  const formattedPolicy = cleanPolicyObject(policy, options)

  // add name and status to policy
  formattedPolicy.displayName = formValues?.policyName || policy.displayName
  formattedPolicy.state = formValues?.policyStatus || policy.state

  return formattedPolicy
}

/**
 * Build the policy payload for the template by filtering tenant specific values before
 * 1. filter specific tenant properties that would make deploy fail on a different tenant
 * 2. fill minimum required values with defaults in case they were filtered
 * 3. build policy payload merging form values into the rawPolicy
 */
export const buildPolicyTemplatePayload = (rawPolicy, formValues) =>
  buildPolicyPayload(fillMinimumRequiredValues(filterTenantProperties(rawPolicy)), formValues)

/**
 * Removes non tenant specific values from policyConfigurations to be used in the template forms
 * @param {object} policyConfigurations
 * @returns the policy configurations with only non tenant specific values
 */
export const sanitizePolicyConfigurations = (policyConfigurations) => ({
  idToNameMappings: {
    directoryRoles: policyConfigurations?.idToNameMappings?.directoryRoles,
  },
  authenticationStrengths: policyConfigurations?.authenticationStrengths?.filter(({ authStrengthId }) =>
    isGlobalAuthStrength(authStrengthId),
  ),
})

/**
 * Creates the options to populate the Select Dropdowns of the inputs
 * @param {object} integratedEmployeesMetaData
 * @param {object} policyConfigurations
 * @param {object} intl
 * @returns the form data with the options for each input
 */
export const getPolicyFormOptions = ({
  integratedEmployeesMetaData = {},
  policyConfigurations = {},
  groups = [],
  intl,
}) => {
  const formOptions = {}
  const { users = [] } = integratedEmployeesMetaData || {}

  const formattedUserOptions = users.map((user) => ({
    ...user,
    label: `${user.label}${user.email ? ` (${user.email})` : ''}`,
  }))
  formOptions.includeUsers = [...BASE_INCLUDE_USERS, ...formattedUserOptions]
  formOptions.excludeUsers = [...BASE_USERS, ...formattedUserOptions]

  // conditions/Locations
  let locations = []
  if (policyConfigurations?.locations?.length > 0) {
    locations = policyConfigurations.locations.map((location) => {
      const formattedLocation = {
        label: `${location?.displayName}` || '',
        value: location?.id || '',
      }

      if (location?.isTrusted === true) {
        formattedLocation.label = `${formattedLocation.label} (Trusted)`
      }

      return formattedLocation
    })
  }

  // Add globals to the top of the list
  formOptions.includeLocations = [...BASE_INCLUDE_LOCATIONS, ...locations]
  formOptions.excludeLocations = [...BASE_LOCATIONS, ...locations]

  // groups
  formOptions.includeGroups = groups
  formOptions.excludeGroups = groups

  // roles
  const roles = Object.entries(policyConfigurations?.idToNameMappings?.directoryRoles || {}).map((entry) => ({
    value: entry[0],
    label: entry[1],
  }))
  formOptions.includeRoles = roles
  formOptions.excludeRoles = roles

  // conditions/Applications
  let applications = []
  if (policyConfigurations?.applications?.length > 0) {
    applications = policyConfigurations.applications.map((app) => {
      const formattedApp = {
        label: `${app?.applicationName}` || '',
        value: app?.applicationId || '',
      }

      return formattedApp
    })
  }

  // Add globals to the top of the list
  formOptions.includeApplications = [...BASE_INCLUDE_APPLICATIONS, ...applications]
  formOptions.excludeApplications = [...BASE_APPLICATIONS, ...applications]

  formOptions.clientAppTypes = [
    {
      label: intl.formatMessage({ id: 'GENERAL.ALL' }),
      value: 'all',
    },
    {
      label: intl.formatMessage({ id: 'SECURE.CLIENT_APPS_CONFIGURE_MODERN_AUTH_CLIENTS_BROWSER' }),
      value: 'browser',
    },
    {
      label: intl.formatMessage({ id: 'SECURE.CLIENT_APPS_CONFIGURE_MODERN_AUTH_CLIENTS_MOBILE_AND_DESKTOP' }),
      value: 'mobileAppsAndDesktopClients',
    },
    {
      label: intl.formatMessage({ id: 'SECURE.CLIENT_APPS_LEGACY_AUTH_CLIENTS_EXCHANGE_ACTIVESYNC' }),
      value: 'exchangeActiveSync',
    },
    {
      label: intl.formatMessage({ id: 'SECURE.CLIENT_APPS_LEGACY_AUTH_CLIENTS_OTHER' }),
      value: 'other',
    },
  ]

  formOptions.excludePlatforms = [
    {
      label: intl.formatMessage({ id: 'POLICY.PLATFORMS.ANDROID' }),
      value: 'android',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.PLATFORMS.IOS' }),
      value: 'iOS',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.PLATFORMS.WINDOWS' }),
      value: 'windows',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.PLATFORMS.WINDOWS_PHONE' }),
      value: 'windowsPhone',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.PLATFORMS.MAC' }),
      value: 'macOS',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.PLATFORMS.LINUX' }),
      value: 'linux',
    },
  ]

  formOptions.includePlatforms = [
    {
      label: intl.formatMessage({ id: 'GENERAL.ALL' }),
      value: 'all',
    },
    ...formOptions.excludePlatforms,
  ]

  formOptions.userRiskLevels = [
    {
      label: intl.formatMessage({ id: 'GENERAL.HIGH' }),
      value: 'high',
    },
    {
      label: intl.formatMessage({ id: 'GENERAL.MEDIUM' }),
      value: 'medium',
    },
    {
      label: intl.formatMessage({ id: 'GENERAL.LOW' }),
      value: 'low',
    },
  ]

  formOptions.signInRiskLevels = [
    {
      label: intl.formatMessage({ id: 'GENERAL.HIGH' }),
      value: 'high',
    },
    {
      label: intl.formatMessage({ id: 'GENERAL.MEDIUM' }),
      value: 'medium',
    },
    {
      label: intl.formatMessage({ id: 'GENERAL.LOW' }),
      value: 'low',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.SIGNIN_RISK.NO_RISK' }),
      value: 'none',
    },
  ]

  formOptions.insiderRiskLevels = [
    {
      label: intl.formatMessage({ id: 'GENERAL.ELEVATED' }),
      value: 'elevated',
    },
    {
      label: intl.formatMessage({ id: 'GENERAL.MODERATE' }),
      value: 'moderate',
    },
    {
      label: intl.formatMessage({ id: 'GENERAL.MINOR' }),
      value: 'minor',
    },
  ]

  formOptions.transferMethods = [
    {
      label: intl.formatMessage({ id: 'POLICY.TRANSFER_METHODS.DEVICE_CODE_FLOW' }),
      value: 'deviceCodeFlow',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.TRANSFER_METHODS.AUTH_TRANSFER' }),
      value: 'authenticationTransfer',
    },
  ]

  formOptions.includeGuestsOrExternalUsers = [
    {
      label: intl.formatMessage({ id: 'POLICY.GUEST_USER.B2B_COLLAB_GUEST' }),
      value: 'b2bCollaborationGuest',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.GUEST_USER.B2B_COLLAB_MEMBER' }),
      value: 'b2bCollaborationMember',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.GUEST_USER.B2B_DIRECT_CONNECT' }),
      value: 'b2bDirectConnectUser',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.GUEST_USER.LOCAL_GUEST' }),
      value: 'internalGuest',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.GUEST_USER.SERVICE_PROVIDER' }),
      value: 'serviceProvider',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.GUEST_USER.OTHER' }),
      value: 'otherExternalUser',
    },
  ]
  formOptions.excludeGuestsOrExternalUsers = formOptions.includeGuestsOrExternalUsers

  formOptions.includeUserActions = [
    {
      label: intl.formatMessage({ id: 'POLICY.USER_ACTIONS.REGISTER_SECURITY_INFO' }),
      value: 'urn:user:registersecurityinfo',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.USER_ACTIONS.REGISTER_JOIN_DEVICES' }),
      value: 'urn:user:registerdevice',
    },
  ]

  formOptions.builtInControls = [
    {
      label: intl.formatMessage({ id: 'POLICY.GRANT_CONTROLS.BLOCK_ACCESS' }),
      value: 'block',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.GRANT_CONTROLS.REQUIRE_MFA' }),
      value: 'mfa',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.GRANT_CONTROLS.REQUIRE_COMPLIANT_DEVICE' }),
      value: 'compliantDevice',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.GRANT_CONTROLS.REQUIRE_MS_ENTRA' }),
      value: 'domainJoinedDevice',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.GRANT_CONTROLS.REQUIRE_APPROVED_CLIENT_APP' }),
      value: 'approvedApplication',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.GRANT_CONTROLS.REQUIRE_APP_PROTECTION' }),
      value: 'compliantApplication',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.GRANT_CONTROLS.REQUIRE_PASSWORD_CHANGE' }),
      value: 'passwordChange',
    },
  ]

  formOptions.grantControlsOperator = [
    {
      label: intl.formatMessage({ id: 'POLICY.OPERATOR.REQUIRE_ALL' }),
      value: 'AND',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.OPERATOR.REQUIRE_ONE' }),
      value: 'OR',
    },
  ]

  // it's not possible to retrieve right now customAuthenticationFactors from MS graph api, see https://augmentt.atlassian.net/browse/AP-7322
  // as a workaround, we are building the options based on policies already created with these values
  // thus if there is an unused customAuthenticationFactor it won't appear in this list
  const customAuthObj = {}
  policyConfigurations?.data?.forEach((item) => {
    item?.rawPolicy?.grantControls?.customAuthenticationFactors?.forEach((authFactor) => {
      customAuthObj[authFactor] = true
    })
  })
  formOptions.customAuthenticationFactors = Object.keys(customAuthObj).map((authFactor) => ({
    label: authFactor,
    value: authFactor,
  }))

  formOptions.authenticationStrength = []
  if (policyConfigurations?.authenticationStrengths?.length > 0) {
    formOptions.authenticationStrength = policyConfigurations?.authenticationStrengths.map((authStrength) => ({
      label: authStrength.displayName,
      value: authStrength.authStrengthId,
    }))
  }

  formOptions.policyStatus = getPolicyStatusOptions(intl)

  return formOptions
}

const mapValuesToLabels = (options) => {
  const result = {}

  options?.forEach((option) => {
    result[option.value] = option.label
  })

  return result
}

/**
 * Returns the form options in a map form { input: { value1: 'label1', value2: 'label2' }, ... }
 * @param {object} formOptions object containing the options for each input
 */
export const getPolicyFormOptionsMap = (formOptions) => {
  const formLabelsMap = {}

  INPUT_LIST.forEach((input) => {
    formLabelsMap[input] = mapValuesToLabels(formOptions?.[input])
  })

  return formLabelsMap
}

const hasGrantControls = (form) =>
  form?.builtInControls?.length || form?.authenticationStrength || form?.customAuthenticationFactors?.length

const hasUsersCondition = (form) =>
  form?.includeUsers?.length ||
  form?.includeGroups?.length ||
  form?.includeRoles?.length ||
  form?.includeGuestsOrExternalUsers?.length

const POLICY_FORM_VALIDATIONS = [
  // conditions inputs
  {
    input: INPUT_KEYS.includeUsers,
    error: (form) => !hasUsersCondition(form),
    errorMessage: 'POLICY.VALIDATION.USER_CONDITIONS_INCLUDE_REQUIRED',
  },
  {
    input: INPUT_KEYS.includeUsers,
    error: (form) =>
      form?.includeUsers?.includes('None') &&
      (form?.includeUsers?.length > 1 || form?.includeGuestsOrExternalUsers?.length),
    errorMessage: 'POLICY.VALIDATION.USER_NONE_COMBINATION',
  },
  {
    input: INPUT_KEYS.includeUsers,
    error: (form) =>
      form?.includeUsers?.includes('All') &&
      !form?.includeUsers?.includes('None') && // Avoid 2 errors saying the same thing for 'All' and 'None'
      (form?.includeUsers?.length > 1 || form?.includeGuestsOrExternalUsers?.length),
    errorMessage: 'POLICY.VALIDATION.USER_ALL_COMBINATION',
  },
  {
    input: INPUT_KEYS.includeUsers,
    error: (form) =>
      form?.includeUsers?.includes('GuestsOrExternalUsers') &&
      !form?.includeUsers?.includes('None') &&
      !form?.includeUsers?.includes('All') && // Avoid 2 errors saying the same thing for 'All' and 'None'
      form?.includeGuestsOrExternalUsers?.length,
    errorMessage: 'POLICY.VALIDATION.USER_GUEST_COMBINATION',
  },
  {
    input: INPUT_KEYS.includeLocations,
    error: (form) => form?.includeLocations?.includes('All') && form?.includeLocations?.length > 1,
    errorMessage: 'POLICY.VALIDATION.INCLUDE_LOCATIONS_ALL_COMBINATION',
  },
  {
    input: INPUT_KEYS.includeLocations,
    error: (form) =>
      form?.includeLocations?.includes('AllTrusted') &&
      !form?.includeLocations?.includes('All') && // Avoid 2 errors saying the same thing for 'All' and 'AllTrusted'
      form?.includeLocations?.length > 1,
    errorMessage: 'POLICY.VALIDATION.INCLUDE_LOCATIONS_ALL_TRUSTED_COMBINATION',
  },
  {
    input: INPUT_KEYS.excludeLocations,
    error: (form) => form?.excludeLocations?.includes('AllTrusted') && form?.excludeLocations?.length > 1,
    errorMessage: 'POLICY.VALIDATION.EXCLUDE_LOCATIONS_ALL_TRUSTED_COMBINATION',
  },
  {
    input: INPUT_KEYS.excludeLocations,
    error: (form) => form?.excludeLocations?.length && !form?.includeLocations?.length,
    errorMessage: 'POLICY.VALIDATION.LOCATIONS_NEED_INCLUDE',
  },
  {
    input: INPUT_KEYS.includeApplications,
    error: (form) => form?.includeApplications?.includes('None') && form?.includeApplications?.length > 1,
    errorMessage: 'POLICY.VALIDATION.INCLUDE_APPLICATIONS_NONE_COMBINATION',
  },
  {
    input: INPUT_KEYS.includeApplications,
    error: (form) =>
      form?.includeApplications?.includes('All') &&
      !form?.includeApplications?.includes('None') && // Avoid 2 errors saying the same thing for 'All' and 'None'
      form?.includeApplications?.length > 1,
    errorMessage: 'POLICY.VALIDATION.INCLUDE_APPLICATIONS_ALL_COMBINATION',
  },
  {
    input: INPUT_KEYS.clientAppTypes,
    error: (form) => !form?.clientAppTypes?.length,
    errorMessage: 'POLICY.VALIDATION.CLIENT_APP_TYPES_REQUIRED',
  },
  {
    input: INPUT_KEYS.clientAppTypes,
    error: (form) => form?.clientAppTypes?.includes('all') && form?.clientAppTypes?.length > 1,
    errorMessage: 'POLICY.VALIDATION.CLIENT_APP_TYPES_ALL_COMBINATION',
  },
  {
    input: INPUT_KEYS.includePlatforms,
    error: (form) => form?.includePlatforms?.includes('all') && form?.includePlatforms?.length > 1,
    errorMessage: 'POLICY.VALIDATION.INCLUDE_PLATFORMS_ALL_COMBINATION',
  },
  // user actions
  // @TODO: when adding session controls to our form we should validate includeUserActions against it
  {
    input: INPUT_KEYS.includeUserActions,
    error: (form) =>
      form?.includeUserActions && (form?.includeApplications?.length || form?.excludeApplications?.length),
    errorMessage: 'POLICY.VALIDATION.USER_ACTIONS_AND_APPLICATIONS',
  },
  {
    input: INPUT_KEYS.customAuthenticationFactors,
    error: (form) =>
      form?.includeUserActions === 'urn:user:registerdevice' && form?.customAuthenticationFactors?.length,
    errorMessage: 'POLICY.VALIDATION.USER_ACTIONS_AND_CUSTOM_AUTH_FACTORS',
  },
  {
    input: INPUT_KEYS.builtInControls,
    error: (form) =>
      form?.includeUserActions === 'urn:user:registerdevice' &&
      (form?.builtInControls?.length > 1 || (form?.builtInControls?.length && !form?.builtInControls?.includes('mfa'))),
    errorMessage: 'POLICY.VALIDATION.USER_ACTIONS_AND_BUILTIN_CONTROLS',
  },
  // grant controls
  {
    input: INPUT_KEYS.builtInControls,
    error: (form) =>
      form?.builtInControls?.includes('block') &&
      (form?.builtInControls?.length > 1 || form?.authenticationStrength || form?.customAuthenticationFactors?.length),
    errorMessage: 'POLICY.VALIDATION.BLOCK_CONTROLS_CONFLICT',
  },
  {
    input: INPUT_KEYS.authenticationStrength,
    error: (form) => form?.authenticationStrength && form?.builtInControls?.includes('mfa'),
    errorMessage: 'POLICY.VALIDATION.AUTH_STRENGTH_MFA_CONFLICT',
  },
  {
    input: INPUT_KEYS.authenticationStrength,
    error: (form) => form?.builtInControls?.includes('passwordChange') && !form?.authenticationStrength,
    errorMessage: 'POLICY.VALIDATION.AUTH_STRENGTH_REQUIRED_WITH_PASSWORD_CHANGE',
  },
  {
    input: INPUT_KEYS.builtInControls,
    error: (form) => form?.builtInControls?.includes('passwordChange') && form?.builtInControls?.length > 1,
    errorMessage: 'POLICY.VALIDATION.PASSWORD_CHANGE_BUILTIN_CONFLICT',
  },
  {
    input: INPUT_KEYS.includeApplications,
    error: (form) => !form?.includeApplications?.includes('All') && form?.builtInControls?.includes('passwordChange'),
    errorMessage: 'POLICY.VALIDATION.PASSWORD_CHANGE_REQUIRE_ALL_APPS',
  },
  {
    input: INPUT_KEYS.grantControlsOperator,
    error: (form) => hasGrantControls(form) && !form.grantControlsOperator,
    errorMessage: 'POLICY.VALIDATION.OPERATOR_REQUIRED',
  },
]

// helper function to build the error object for the validations
const updateErrorsObject = (errors, input, errorToInsert) => {
  if (errors[input]) {
    errors[input].push(errorToInsert)
  } else {
    errors[input] = [errorToInsert] // eslint-disable-line no-param-reassign
  }
}

export const validatePolicyForm = (form, policyFormOptionsMap, intl) => {
  const errors = {}

  // custom validations
  POLICY_FORM_VALIDATIONS.forEach((validation) => {
    if (validation.error(form)) {
      const errorMessage = intl.formatMessage({ id: validation.errorMessage })
      updateErrorsObject(errors, validation.input, errorMessage)
    }
  })

  // input value validation - check if input values exist as options
  // since user can add anything when editing policy through json editor
  INPUT_LIST.forEach((input) => {
    if (!policyFormOptionsMap?.[input]) {
      return // if options were not loaded return
    }

    const arrayValues = toArray(form[input])
    arrayValues.forEach((value) => {
      if (!policyFormOptionsMap?.[input]?.[value]) {
        const errorMessage = intl.formatMessage({ id: 'POLICY_MANAGEMENT.INVALID_VALUE' }, { value })
        updateErrorsObject(errors, input, errorMessage)
      }
    })
  })

  return errors
}

/**
 * Returns an map with the inputs that are filled in the form
 */
export const getInitialVisibleInputs = (form) => {
  const visibleInputsMap = {}

  // grant controls and general inputs are always visible
  GRANT_CONTROLS_INPUTS_LIST.concat(GENERAL_INPUTS_LIST).forEach((input) => {
    visibleInputsMap[input] = true
  })

  // show conditional inputs initially only if they have values
  CONDITIONS_INPUTS_LIST.forEach((input) => {
    visibleInputsMap[input] = !isEmpty(form?.[input])
  })

  return visibleInputsMap
}

/**
 * Policy schema
 * Note that here we are type checking the policy json itself (which is different from the form schema)
 */
export const policySchema = yup.object().shape({
  // partialEnablementStrategy: null, // @TODO: validate this property?
  displayName: yup.string(),
  grantControls: yup
    .object()
    .nullable()
    .shape({
      authenticationStrength: null,
      // termsOfUse: [], // @TODO: validate when we start supporting it in the form
      customAuthenticationFactors: yup.array().of(yup.string()),
      builtInControls: yup.array().of(yup.string()),
      operator: yup.string(),
    }),
  state: yup.string(),
  sessionControls: yup.object().nullable(), // @TODO: validate properties when we start supporting it in the form
  conditions: yup.object().shape({
    clientAppTypes: yup.array().of(yup.string()),
    // times: null, // @TODO: validate when we start supporting it in the form
    // clientApplications: null, // @TODO: validate when we start supporting it in the form
    // devices: null, // @TODO: validate when we start supporting it in the form
    locations: yup
      .object()
      .nullable()
      .shape({
        includeLocations: yup.array().of(yup.string()),
        excludeLocations: yup.array().of(yup.string()),
      }),
    signInRiskLevels: yup.array().of(yup.string()),
    userRiskLevels: yup.array().of(yup.string()),
    insiderRiskLevels: yup.string().nullable(),
    users: yup.object().shape({
      includeRoles: yup.array().of(yup.string()),
      excludeGuestsOrExternalUsers: yup
        .object()
        .nullable()
        .shape({
          guestOrExternalUserTypes: yup.string(),
          externalTenants: yup.object().shape({
            membershipKind: yup.string(),
          }),
        }),
      excludeGroups: yup.array().of(yup.string()),
      excludeRoles: yup.array().of(yup.string()),
      excludeUsers: yup.array().of(yup.string()),
      includeUsers: yup.array().of(yup.string()),
      includeGuestsOrExternalUsers: yup
        .object()
        .nullable()
        .shape({
          guestOrExternalUserTypes: yup.string(),
          externalTenants: yup.object().shape({
            membershipKind: yup.string(),
          }),
        }),
      includeGroups: yup.array().of(yup.string()),
    }),
    platforms: yup
      .object()
      .nullable()
      .shape({
        includePlatforms: yup.array().of(yup.string()),
        excludePlatforms: yup.array().of(yup.string()),
      }),
    // deviceStates: null, // @TODO: validate when we start supporting it in the form
    applications: yup.object().shape({
      includeUserActions: yup.array().of(yup.string()),
      // includeAuthenticationContextClassReferences: [], // @TODO: validate when we start supporting it in the form
      includeApplications: yup.array().of(yup.string()),
      // applicationFilter: null, // @TODO: validate when we start supporting it in the form
      excludeApplications: yup.array().of(yup.string()),
    }),
  }),
})

/**
 * Validates the raw policy (json) schema, returns an array with the validation errors
 * @param {object} rawPolicy
 */
export const validatePolicySchema = (rawPolicy) => {
  let errors = []
  try {
    policySchema.validateSync(rawPolicy, { abortEarly: false, strict: true })
  } catch (err) {
    errors = err.errors
  }
  return errors
}

/**
 * Validate fields that could be in the json editor and are not in the form (so are not validated by the form validation)
 * Differs from the `validatePolicySchema` which is schema specific
 */
export const validateEditorPayload = (rawPolicy, intl) => {
  const errors = []

  const nonEditableFields = ['id', 'createdDateTime', 'modifiedDateTime']
  nonEditableFields.forEach((field) => {
    if (rawPolicy?.[field]) {
      errors.push(intl.formatMessage({ id: 'POLICY_TEMPLATES.FIELD_NOT_EDITABLE' }, { field }))
    }
  })

  return errors
}
